import React, { useState } from "react";

import { makeStyles } from "tss-react/mui";

import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";

import CloseIcon from "@mui/icons-material/Close";
import PercentIcon from "@mui/icons-material/Percent";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import DqmClass from "../../../service/dqm";
import { KMKDrawer, ButtonUI } from "../../Common";
import Switch from "../../Common/Switch";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/actions";

const drawerWidth = 480;
const alerts = ["Findings", "Health"];
const alertsMessage = {
  Findings: "Number of records which did not pass DQM Rules.",
  Health: "% of total records which did not pass DQM Rules.",
};
const conditions = [
  { key: "=", value: "=" },
  { key: "<", value: "<" },
  { key: ">", value: ">" },
  { key: "<=", value: "<=" },
  { key: ">=", value: ">=" },
  { key: "!=", value: "!=" },
];

const useStyles = makeStyles()((theme) => ({
  heading: {
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    "& span": {
      fontWeight: "normal",
    },
  },
  heading2: {
    fontWeight: "bold",
    fontSize: "1rem",
    "& span": {
      fontWeight: "normal",
    },
  },
  closeIcon: {
    padding: 0,
  },
  container: {
    height: "100%",
    padding: "15px 20px",
    "& .errorMessage .MuiPaper-root": {
      marginTop: "20px",
      alignItems: "flex-start",
      border: "none",
    },
    "& .errorMessage .MuiAlert-message": {
      color: "#C72C2C",
    },
    "& .errorMessage": {
      marginBottom: 0,
    },
  },
  alertMessage: {
    "& .MuiPaper-root": {
      marginTop: "9px",
      alignItems: "center",
      borderRadius: 10,
    },
    "& .MuiFormControl-root": { color: "#a2a2a2" },
    "& .MuiAlert-icon": {
      padding: 0,
      backgroundColor: "#67A7D5",
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "9px",
      height: "71px",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        height: "100%",
        color: "#FFFFFF",
      },
    },
    "& .MuiAlert-message": {
      padding: "15px 5px",
      color: "#A2A2A2",
    },
  },
}));

const AlertWhenPanel = ({ Workspace_id, alertField, toggleAlert, updateAlertWhen }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [v1, v2, v3] = alertField.AlertWhen ? alertField.AlertWhen.split(" ") : ["", "", ""];

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(!!alertField.AlertWhen);
  const [type, setType] = useState(v1);
  const [condition, setCondition] = useState(v2);
  const [value, setValue] = useState(v3);

  const handleSave = async () => {
    setLoading(true);

    const dqmClass = new DqmClass();
    const response = await dqmClass.updateDqmRule(
      Workspace_id,
      alertField.DQMRule_id,
      alert ? { AlertWhen: `${type} ${condition} ${value}` } : { AlertWhen: null }
    );

    if (response.success) {
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
      updateAlertWhen(alertField.DQMRule_id, alert ? `${type} ${condition} ${value}` : null);
    }
    setLoading(false);
  };

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Toolbar />
      <Box className={classes.container} role="presentation">
        <Grid container spacing={2}>
          <Grid size={10}>
            <Typography className={classes.heading} variant="h6" gutterBottom component="div">
              Alert Configuration
            </Typography>
          </Grid>
          <Grid size={2}>
            <Button className={classes.closeIcon} onClick={toggleAlert}>
              <CloseIcon />
            </Button>
          </Grid>
        </Grid>

        <Typography className={classes.heading2} variant="h6" gutterBottom component="div">
          Attribute: <span>{alertField.Attribute}</span>
        </Typography>

        <FormControl className={classes.FormControl} sx={{ marginTop: "10px" }} component="fieldset">
          <FormGroup aria-label="position" row>
            <FormControlLabel
              value={alert}
              control={<Switch />}
              label="Trigger Alert When"
              labelPlacement="start"
              sx={{ margin: "0" }}
              onChange={(e) => setAlert((prev) => !prev)}
              checked={alert}
              margin="dense"
            />
          </FormGroup>
        </FormControl>

        <Grid container spacing={1}>
          <Grid size={4}>
            <TextField
              select
              required
              fullWidth
              variant="outlined"
              sx={{ my: 2 }}
              disabled={!alert}
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setValue("");
              }}
              label="Trigger"
            >
              {alerts.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={4}>
            <TextField
              select
              required
              fullWidth
              variant="outlined"
              sx={{ my: 2 }}
              disabled={!alert}
              label="Operator"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
            >
              {conditions.map((condition) => (
                <MenuItem key={condition.key} value={condition.key}>
                  {condition.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={4}>
            <TextField
              required
              value={value}
              onChange={(e) => {
                if (isNaN(e.target.value)) return;
                if (e.target.value.length > (type === "Health" ? 3 : 10)) return;
                if (type === "Health" && parseInt(e.target.value) > 100) {
                  setValue(100);
                  return;
                }

                setValue(parseInt(e.target.value) < 0 ? e.target.value * -1 : e.target.value);
              }}
              fullWidth
              label="Threshold"
              variant="outlined"
              sx={{ my: 2 }}
              disabled={!alert}
              InputProps={{
                ...(type === "Health"
                  ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <PercentIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }
                  : {}),
              }}
            />
          </Grid>
        </Grid>

        {type && (
          <Stack className={classes.alertMessage} sx={{ width: "100%", marginBottom: "20px" }} spacing={2}>
            <Alert
              icon={<InfoOutlinedIcon sx={{ margin: "10px", color: "#67A7D5" }} fontSize="inherit" />}
              sx={{
                backgroundColor: "#ECF7FF",
                border: "1px solid #67A7D5",
                padding: "0",
                marginRight: "2px",
              }}
              variant="outlined"
              severity="info"
            >
              {alertsMessage[type]}
            </Alert>
          </Stack>
        )}

        <Box my={2}>
          <ButtonUI
            disabled={(alert && !(type && condition && value)) || loading}
            variant="contained"
            color="success"
            onClick={handleSave}
          >
            Save
          </ButtonUI>
          <ButtonUI
            variant="contained"
            color="secondary"
            style={{ marginLeft: 15 }}
            onClick={toggleAlert}
            disabled={loading}
          >
            Cancel
          </ButtonUI>
        </Box>
      </Box>
    </KMKDrawer>
  );
};

export default AlertWhenPanel;
